import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "./api";
import { ProfileThumb } from "./ProfileLibrary";
import { StatusBadge } from "./ResultsPanel";

const ROWS = [
  ["Mullion", (r) => r.mullion], ["Transom", (r) => r.transom], ["Sightline (face width)", (r) => `${r.sightline_mm} mm`],
  ["Mullion depth", (r) => `${r.mullion_depth_mm} mm`], ["Mullion weight", (r) => `${r.mullion_kg_m} kg/m`],
  ["Aluminium (frame + parts)", (r) => `${r.aluminium_kg} kg`], ["Aluminium per m²", (r) => `${r.kg_per_m2} kg/m²`],
  ["Max utilisation", (r) => `${Math.round(r.max_utilization * 100)}%`], ["Governing check", (r) => r.governing],
  ["Grid", (r) => `${r.grid.cols} × ${r.grid.rows} · ${r.grid.module_w_mm} × ${r.grid.module_h_mm} mm`],
];

const SectionThumb = ({ libId }) => {
  const [p, setP] = useState(null);
  useEffect(() => { if (libId) api.get(`/profiles/${libId}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [libId]);
  return <div className="h-40 rounded-md border border-zinc-800 bg-[#0c0c0f] p-2">{p ? <ProfileThumb p={p} className="h-36" /> : <Loader2 className="h-4 w-4 animate-spin text-zinc-600" />}</div>;
};

export default function BwayCompare({ project, zone, open, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [support, setSupport] = useState("simple");
  const nav = useNavigate();
  useEffect(() => {
    if (!open || !zone) return;
    setData(null);
    api.post(`/projects/${project.id}/zones/${zone.id}/compare`, { mullion_support: support }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e, "Compare failed")); onClose(); });
  }, [open, zone?.id, support]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (row) => {
    setBusy(row.brand);
    try {
      const { data: d } = await api.post(`/projects/${project.id}/designs`, { name: `${zone.label} · ${row.label}`, system_type: "curtain_wall", inputs: row.inputs });
      toast.success(`${d.name} saved`);
      onClose();
      if (row.brand === "bway") nav(`/bway?project=${project.id}&design=${d.id}`);
      else onSaved(d);
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-zinc-800 bg-[#121215]" data-testid="bway-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Manrope]">BWAY Slim Pro vs Bhoruka · {zone?.label}</DialogTitle>
          <DialogDescription>{data ? `${data.zone.width_m} × ${data.zone.height_m} m zone · ${data.basis}` : "Designing both systems…"}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Mullion support</span>
          <Select value={support} onValueChange={setSupport}>
            <SelectTrigger data-testid="compare-support-select" className="h-8 w-60 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="simple">Simple span (1 floor)</SelectItem>
              <SelectItem value="continuous_2">Continuous over 2 floors</SelectItem>
              <SelectItem value="continuous_3">Continuous over 3 floors</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!data ? <Loader2 className="h-6 w-6 animate-spin text-zinc-500" /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              {data.rows.map((r) => (
                <div key={r.brand} className="space-y-2" data-testid={`compare-col-${r.brand}`}>
                  <div className="flex items-center justify-between"><span className="font-[Manrope] font-bold text-white">{r.label}</span><StatusBadge status={r.overall_status} testid={`compare-status-${r.brand}`} /></div>
                  <SectionThumb libId={r.members[0].lib_id} />
                  <div className="font-mono text-[10px] text-zinc-500">{r.design_code}</div>
                </div>
              ))}
            </div>
            <table className="w-full text-sm" data-testid="compare-table">
              <tbody>
                {ROWS.map(([label, fn]) => (
                  <tr key={label} className="border-t border-zinc-800">
                    <td className="py-1.5 pr-3 text-zinc-500">{label}</td>
                    {data.rows.map((r) => <td key={r.brand} className="py-1.5 font-mono text-xs text-zinc-200" data-testid={`compare-${r.brand}-${label.split(" ")[0].toLowerCase()}`}>{fn(r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 font-mono text-xs text-sky-200" data-testid="compare-delta">
              <span>Sightline Δ {data.delta.sightline_mm > 0 ? "+" : ""}{data.delta.sightline_mm} mm</span>
              <span>Aluminium Δ {data.delta.aluminium_kg > 0 ? "+" : ""}{data.delta.aluminium_kg} kg ({data.delta.aluminium_pct}%)</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {data.rows.map((r) => (
                <Button key={r.brand} data-testid={`compare-save-${r.brand}`} size="sm" disabled={!!busy} onClick={() => save(r)} variant={r.brand === "bway" ? "default" : "outline"} className={r.brand === "bway" ? "bg-sky-500 text-white hover:bg-sky-600" : "border-zinc-700"}>
                  {busy === r.brand ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}Save {r.label} design
                </Button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
